import React, { useContext, useEffect, useRef } from 'react';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { IMenuDTO, ISystemDTO, MenuType } from 'src/components/types';
import { log } from 'src/services/LogService';
import { isDefined } from 'src/utils/TypeCheckers';
import { SessionContext } from 'src/providers/session/SessionContext';
import { GuslUser } from 'src/providers/session/types';
import { SystemContext } from './SystemContext';
import { getCacheEntry, setCacheEntry } from 'src/providers/cache/inMemoryCache';

const getBlankConfig = (): ISystemDTO => {
  return { menuGroups: [] } as ISystemDTO;
};

const SYSTEM_CACHE_KEY = '/form/system';
const SYSTEM_CACHE_TTL_SECONDS = 29;

/** Module-level in-flight deduplication — mirrors useVXQuery pattern */
let inFlightSystemRequest: Promise<ISystemDTO | null> | null = null;

export const SystemProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const sessionContext = useContext(SessionContext);

  const classNameRef = useRef<string>('SystemProvider-' + new Date().getTime());
  const className = classNameRef.current;

  const menuMapRef = useRef<Map<string, IMenuDTO>>(new Map<string, IMenuDTO>());

  const systemLoadedSubject = useRef<BehaviorSubject<boolean>>(
    new BehaviorSubject<boolean>(false),
  ).current;

  const systemConfigSubject = useRef<BehaviorSubject<ISystemDTO>>(
    new BehaviorSubject<ISystemDTO>(getBlankConfig()),
  ).current;

  const filterMenuTypes = (menuType: MenuType | undefined): boolean => {
    if (!menuType) {
      return false;
    }

    switch (menuType) {
      case MenuType.ENTITY_GROUP:
      case MenuType.SINGLE_PAGE:
        return true;
      default:
        return false;
    }
  };

  const createMenuMap = (config: ISystemDTO) => {
    const menuMap = menuMapRef.current;
    menuMap.clear();

    if (config?.menuGroups) {
      config.menuGroups.forEach((menuGroup) => {
        if (menuGroup?.menuItems) {
          menuGroup.menuItems
            .filter((item) => filterMenuTypes(item.menuType))
            .forEach((item) => {
              if (item.code) {
                menuMap.set(item.code.toLowerCase(), item);
              }
            });
        }
      });
    }
  };

  const setSystemConfig = (config: ISystemDTO): void => {
    if (!isDefined(config)) {
      log.warn(className, 'MSG004', 'Attempted to set empty system config');
      return;
    }

    createMenuMap(config);
    systemConfigSubject.next(config);
    systemLoadedSubject.next(true);
  };

  const loadConfig = () => {
    // 1. Check in-memory cache first
    const cached = getCacheEntry<ISystemDTO>(SYSTEM_CACHE_KEY);
    if (cached) {
      setSystemConfig(cached);
      return;
    }

    // 2. Deduplicate concurrent in-flight requests
    if (!inFlightSystemRequest) {
      inFlightSystemRequest = sessionContext
        .get<ISystemDTO>('/form/system')
        .then((response) => {
          const config: ISystemDTO = { ...response.data } as ISystemDTO;
          if (isDefined(config)) {
            setCacheEntry(SYSTEM_CACHE_KEY, config, SYSTEM_CACHE_TTL_SECONDS);
          }
          return config;
        })
        .catch((error) => {
          log.info(className, 'ERR001', 'Error loading system config', error);
          return null;
        })
        .finally(() => {
          inFlightSystemRequest = null;
        });
    }

    inFlightSystemRequest.then((config) => {
      if (isDefined(config)) {
        setSystemConfig(config!);
      }
    });
  };

  // Single trigger: only load system config when a user logs in.
  // This eliminates the previous duplicate caused by both a mount-time
  // effect and a watchLogin subscription firing during initial load.
  useEffect(() => {
    const subscription: Subscription | undefined = sessionContext
      .watchLogin?.()
      ?.subscribe((user: GuslUser | null) => {
        if (isDefined(user)) {
          loadConfig();
        } else {
          setSystemConfig(getBlankConfig());
          systemLoadedSubject.next(false);
        }
      });

    return () => {
      subscription?.unsubscribe();
    };
  }, [sessionContext]);

  const getSystemConfig = (): ISystemDTO => {
    return systemConfigSubject.getValue();
  };

  const watchSystemConfig = (): Observable<ISystemDTO> => {
    return systemConfigSubject.asObservable();
  };

  const getMenuConfig = (code: string): IMenuDTO | undefined => {
    const value = menuMapRef.current.get(code.toLowerCase());

    if (!value) {
      log.warn(className, 'MSG002', 'Failed to find menu code for [' + code + ']');
    }

    return value;
  };

  const getMenuConfigFromPath = (path: string): IMenuDTO | undefined => {
    const strings = path.split('/').filter(Boolean);

    if (strings.length >= 1) {
      return getMenuConfig(strings[strings.length - 1]);
    }

    log.warn(className, 'MSG003', 'Failed to find menu code from', path);
    return undefined;
  };

  const watchSystemLoaded = (): Observable<boolean> => {
    return systemLoadedSubject.asObservable();
  };

  return (
    <SystemContext.Provider
      value={{
        getSystemConfig,
        setSystemConfig,
        getMenuConfig,
        getMenuConfigFromPath,
        watchSystemConfig,
        watchSystemLoaded,
      }}
    >
      {children}
    </SystemContext.Provider>
  );
};
